import { z } from "zod";

import { EQUIPMENT_DISTRIBUTION, INPUT_DISTRIBUTION } from "./constants";

// Mirrors the legacy `formResolver` (Form/index.js): a field is invalid only
// when its value is falsy (undefined / null / empty string). Truthy values —
// including a non-empty string, a selected option object, or an array — pass.
// This intentionally reproduces the previous behavior 1:1 (e.g. an empty array
// is treated as valid by the original `!value` check).
const requiredTruthy = (message: string) => z.custom<unknown>(value => !!value, { message });

export interface InfoHubFormSchemaMessages {
  required: string;
  quantityPositive: string;
}

// Reads the `.value` of a select-option-shaped field.
const optionValue = (field: unknown): unknown =>
  (field as { value?: unknown } | null | undefined)?.value;

export const createInfoHubFormSchema = ({
  required,
  quantityPositive,
}: InfoHubFormSchemaMessages) =>
  z
    .looseObject({
      content_type: requiredTruthy(required),
      name: requiredTruthy(required),
      topics: requiredTruthy(required),
      content: requiredTruthy(required),
      aggregators: requiredTruthy(required),
      status: z.string().optional(),
    })
    // The distribution fields are required only for their matching content type.
    .superRefine((data, ctx) => {
      const contentType = optionValue(data.content_type);

      const requireField = (path: string, isPresent: boolean, message: string) => {
        if (!isPresent) {
          ctx.addIssue({ code: "custom", path: [path], message });
        }
      };

      const isPositiveQuantity = (value: unknown) =>
        value !== undefined && value !== null && value !== "" && Number(value) > 0;

      if (contentType === INPUT_DISTRIBUTION) {
        requireField(
          "input_distribution_category",
          !!optionValue(data.input_distribution_category),
          required
        );
        requireField("input_distribution_details", !!data.input_distribution_details, required);
        requireField(
          "input_distribution_unit_of_measure",
          !!optionValue(data.input_distribution_unit_of_measure),
          required
        );
        requireField(
          "input_distribution_quantity",
          isPositiveQuantity(data.input_distribution_quantity),
          quantityPositive
        );
      }

      if (contentType === EQUIPMENT_DISTRIBUTION) {
        requireField(
          "equipment_distribution_type",
          !!optionValue(data.equipment_distribution_type),
          required
        );
        requireField(
          "equipment_distribution_details",
          !!data.equipment_distribution_details,
          required
        );
        requireField(
          "equipment_distribution_quantity",
          isPositiveQuantity(data.equipment_distribution_quantity),
          quantityPositive
        );
      }
    });
